// Ruang Kendali (the overview, `/`): header, KPI row, map, side panel, distribution flow.
// Formal and calm: no exclamation marks, no "…" in action labels; "Titik Distribusi" and "Reader" in every sentence.
// Numbers arrive formatted (strings); a count that changes the wording arrives as a number too.

export const OVERVIEW = {
  title: 'Ruang Kendali',
  loading: 'Memuat Ruang Kendali…',
  region: 'Wilayah yang ditampilkan',
  national: 'Seluruh Indonesia',
  allRegencies: 'Seluruh kabupaten/kota',
  regency: 'Kabupaten/kota',
  monitor: 'Mode Layar Monitor',
  monitorHint: 'Mode Layar Monitor (M)',
  monitorShort: 'Layar monitor',
  demoLead: 'Mode demo.',
  demoRest: 'Tabung berseri DEMO-CNG adalah data simulasi. Reader di meja presentasi membaca tabung sungguhan.',
  kpisLabel: 'Angka utama',
  headerLabel: 'Keadaan hari ini',
} as const;

/** The KPI row: the same terms as the Tabung page. */
export const OVERVIEW_KPI = {
  active: 'Tabung aktif',
  activeSub: (registered: string) => `${registered} terdaftar`,
  activeToday: (n: string) => `+${n} didaftarkan hari ini`,
  transit: 'Dalam pengiriman',
  transitSub: (trucks: string) => `tabung · ${trucks} truk`,
  today: 'Surat Jalan hari ini',
  todaySub: (departed: string, received: string) => `${departed} berangkat · ${received} diterima`,
  missing: 'Tidak ditemukan',
  missingSub: (n: number) => (n ? 'perlu ditelusuri' : 'semua ditemukan'),
  readers: 'Reader terhubung',
  /** `down`: how many are not connected, formatted; null when there is no Reader at all */
  readersSub: (down: string | null, allUp: boolean) => (down === null ? 'belum ada Reader' : allUp ? 'semua terhubung' : `${down} tidak terhubung`),
  action: 'Perlu tindakan',
  /** `critical`: formatted, null when there is none; `any`: whether anything is open */
  actionSub: (critical: string | null, any: boolean) => (!any ? 'tidak ada' : critical ? `${critical} kritis` : 'tidak ada yang kritis'),
  of: (n: string) => `/ ${n}`,
  /** the tile's accessible name: "Tabung aktif: 150, 150 terdaftar. Lihat daftar" */
  aria: (label: string, value: string, sub: string) => `${label}: ${value}, ${sub}. Lihat daftar`,
  ofAria: (n: string) => ` dari ${n}`,
  sparkTitle: (caption: string) => `${caption}, 14 hari terakhir`,
  sparks: {
    reads: 'Tabung terbaca per hari',
    departed: 'Surat Jalan berangkat per hari',
    received: 'Surat Jalan diterima per hari',
    discrepancy: 'Surat Jalan diterima dengan selisih per hari',
  },
} as const;

/** The side panel: tabs and their bounded feeds. */
export const OVERVIEW_PANEL = {
  label: 'Aktivitas dan tindakan',
  tabsLabel: 'Isi panel',
  collapse: 'Ciutkan panel',
  expand: 'Buka panel aktivitas',
  urgent: (n: string) => `${n} perlu tindakan`,
  tabs: {
    start: 'Mulai',
    startName: 'Langkah pertama',
    feed: 'Aktivitas',
    feedName: 'Aktivitas terbaru',
    today: 'Surat Jalan',
    todayName: (n: string | null) => (n ? `Surat Jalan hari ini, ${n}` : 'Surat Jalan hari ini'),
    action: 'Tindakan',
    actionName: (n: string | null) => (n ? `Perlu tindakan, ${n}` : 'Perlu tindakan'),
  },
  seeAll: 'Lihat semua',
  seeAllFeed: 'Lihat semua Surat Jalan hari ini',
  seeAllToday: 'Lihat semua Surat Jalan hari ini',
  seeAllAction: 'Lihat semua di Peringatan',
  feedEmpty: 'Belum ada aktivitas hari ini. Muat, berangkat, bongkar, dan tabung yang terbaca tanpa Surat Jalan tampil di sini.',
  todayEmpty: 'Belum ada Surat Jalan hari ini.',
  todayEmptyOperate: 'Buat Surat Jalan',
  actionEmpty: 'Tidak ada yang perlu ditindaklanjuti.',
  /** a Tindakan row's link: the verb, and its accessible name with what it opens */
  cta: { open: 'Lihat', trace: 'Telusuri', check: 'Periksa' },
  ctaAria: (cta: string, title: string) => `${cta}: ${title}`,
  moreSeen: (n: string) => `${n} tabung lain terbaca tanpa Surat Jalan`,
} as const;

/** The panel's rows: line 1 the subject, line 2 "what happened · how many · where" (counts arrive formatted). */
export const OVERVIEW_FEED = {
  loading: 'Memuat',
  cancelled: 'Dibatalkan',
  departed: 'Berangkat',
  unloading: 'Dibongkar',
  received: 'Diterima',
  discrepancy: (missing: string) => `Diterima, selisih ${missing}`,
  seen: 'Terbaca tanpa Surat Jalan',
  cylinders: (n: string) => `${n} tabung`,
  matched: (got: string, all: string) => `${got} dari ${all} tabung`,
  route: (from: string, to: string) => `${from} → ${to}`,
  cylinder: 'Tabung',
  /** one quiet word in the meta line of simulated data (the page already shows the demo banner) */
  demo: 'Demo',
  sep: ' · ',
} as const;

/** "Mulai": the four setup steps (admins only, until done). */
export const OVERVIEW_SETUP = {
  intro: 'Sistem siap dipakai setelah empat langkah ini selesai.',
  progress: (done: string, all: string) => `${done} dari ${all} selesai`,
  progressLabel: 'Langkah pertama',
  next: ' (langkah berikutnya)',
  doneAria: 'Selesai',
  stepAria: (n: number) => `Langkah ${n}`,
  byAdmin: 'Dilakukan oleh administrator.',
  byOperator: (wh: string) => `Dilakukan oleh petugas ${wh}.`,
  afterPrevious: 'Dapat dimulai setelah langkah sebelumnya selesai.',
  steps: {
    warehouse: { title: (wh: string) => `Tambahkan ${wh}`, text: (wh: string) => `Satu Titik Distribusi berjenis ${wh}: tempat tabung didaftarkan dan kiriman pertama berangkat.`, action: 'Atur Titik Distribusi' },
    reader: { title: (wh: string) => `Tempatkan Reader di ${wh}`, text: (wh: string) => `Nyalakan Reader; Reader tampil di halaman Reader dalam beberapa detik. Pilih Tempatkan, lalu ${wh}.`, action: 'Buka Reader' },
    cylinders: { title: 'Daftarkan tabung', text: 'Dekatkan tag tabung ke Reader gudang, lalu simpan; nomor seri dibuat otomatis.', action: 'Daftarkan tabung' },
    shipment: { title: 'Buat Surat Jalan pertama', text: 'Pilih tujuan, isi kendaraan dan pengemudi, lalu lewatkan tabung di depan Reader. Surat Jalan terbit saat berangkat.', action: 'Buat Surat Jalan' },
  },
} as const;

/** "Alur distribusi": where the active cylinders are now, and 14 days of Surat Jalan. */
export const OVERVIEW_FLOW = {
  title: 'Alur distribusi',
  collapse: 'Ciutkan alur distribusi',
  asOf: (t: string) => `Data per ${t}`,
  hiddenLabel: 'Angka disembunyikan',
  hidden: '<5 = kurang dari 5 rumah tangga; angka disembunyikan agar rumah tangga tidak dapat dikenali.',
  expand: 'Buka alur distribusi',
  trendTitle: 'Surat Jalan 14 hari',
  label: 'Tampilan alur',
  now: 'Sekarang',
  days: '14 hari',
  trendMissing: 'Tren belum tersedia',
  warehouse: (wh: string) => `Di ${wh}`,
  transit: 'Dalam pengiriman',
  transitSub: (trucks: string) => `${trucks} truk`,
  places: 'Di Titik Distribusi',
  placesSub: (n: string) => `${n} Titik Distribusi`,
  fillingStations: 'Di SPBG',
  hubs: 'Di Hub',
  hubsFull: (n: string) => `${n} isi, siap antar`,
  workshops: 'Di Bengkel Uji',
  households: 'Di warga',
  couriers: 'Dibawa kurir',
  destroyed: 'Dimusnahkan',
  inToday: (n: string) => `${n} masuk hari ini`,
  handedToday: (n: string) => `${n} diserahkan hari ini`,
  trucksByKind: (distribution: string, withdrawal: string | null) => `${distribution} distribusi${withdrawal ? ` · ${withdrawal} penarikan` : ''}`,
  couriersOut: (out: string, late: string | null) => `${out} pengantaran${late ? ` · ${late} terlambat` : ''}`,
  destroyedToday: (n: string) => `${n} hari ini`,
  stageAria: (label: string, value: string, sub?: string) => `${label}: ${value} tabung${sub ? `, ${sub}` : ''}. Lihat daftar`,
  todayLine: (departed: string, received: string) => `Hari ini ${departed} Surat Jalan berangkat · ${received} diterima`,
  trendLabel: 'Surat Jalan per hari, 14 hari terakhir',
  trendEmpty: 'Belum ada Surat Jalan dalam 14 hari terakhir',
  trendEmptyText: 'Grafik tampil setelah kiriman pertama berangkat.',
  departed: 'Berangkat',
  received: 'Diterima',
  discrepancy: 'Dengan selisih',
} as const;

/** The map: controls, layers, legend, bubbles, the truck card. */
export const OVERVIEW_MAP_COPY = {
  label: 'Peta Titik Distribusi dan kiriman berjalan',
  zoomIn: 'Perbesar',
  zoomOut: 'Perkecil',
  layers: 'Lapisan',
  basemap: 'Peta dasar',
  basemapMap: 'Peta',
  basemapSatellite: 'Satelit',
  show: 'Tampilkan',
  layerPlaces: 'Titik Distribusi',
  layerTrips: 'Kiriman berjalan',
  layerCouriers: 'Kurir',
  layerReaders: 'Reader',
  grouping: 'Tampilan peta',
  groupingCluster: 'Klaster',
  groupingProvince: 'Per provinsi',
  focus: 'Fokus ke wilayah',
  fullscreen: 'Layar penuh',
  fullscreenExit: 'Keluar dari layar penuh',
  loading: 'Memuat peta',
  failed: 'Peta belum dapat dimuat.',
  retry: 'Coba lagi',
  capped: 'Perbesar peta untuk melihat semua kiriman',
  emptyTitle: 'Belum ada Titik Distribusi berkoordinat',
  emptyText: 'Isi lintang dan bujur pada data Titik Distribusi agar tampil di peta.',
  emptyAction: 'Atur Titik Distribusi',
  legend: {
    label: 'Keterangan peta (posisi kendaraan perkiraan, bukan GPS)',
    places: 'Titik Distribusi',
    trip: 'Truk distribusi',
    late: 'Melewati perkiraan tiba',
    withdrawal: 'Truk penarikan',
    courier: 'Kurir',
    toggle: 'Legenda',
    action: 'Perlu tindakan',
    readerDown: 'Reader tidak terhubung',
  },
  area: {
    /** `trucks` / `action`: formatted, null when none */
    name: (name: string, cylinders: string, locations: string, trucks: string | null, action: string | null) =>
      `${name}: ${cylinders} tabung, ${locations} Titik Distribusi${trucks ? `, ${trucks} truk dalam pengiriman` : ''}${action ? `, ${action} perlu tindakan` : ''}. Tekan Enter untuk memperbesar.`,
    cluster: (locations: string, cylinders: string) => `${locations} Titik Distribusi, ${cylinders} tabung. Tekan Enter untuk memperbesar.`,
    tipCylinders: (n: string) => `${n} tabung`,
    tipPlaces: (n: string) => `${n} Titik Distribusi`,
    tipReaders: (online: string, total: string | null) => (total ? `Reader ${online} dari ${total} terhubung` : 'Belum ada Reader'),
    tipTrucks: (n: string) => `${n} truk dalam pengiriman`,
    tipAction: (n: string, critical: string | null) => `${n} perlu tindakan${critical ? ` (${critical} kritis)` : ''}`,
  },
  spot: {
    name: (name: string, cylinders: string, readers: string) => `${name}, ${cylinders} tabung, ${readers}. Tekan Enter untuk rincian.`,
    readersNone: 'belum ada Reader',
    readersDown: 'Reader tidak terhubung',
    readersOk: 'Reader terhubung',
  },
  items: {
    toggle: (n: string) => `Daftar di peta (${n})`,
    label: 'Kendaraan dan kurir di peta',
    noun: 'kendaraan',
    late: 'Melewati perkiraan tiba',
  },
  hover: {
    distribution: 'Distribusi',
    eta: (t: string) => `perkiraan tiba ${t} WIB`,
    courierTimes: (from: string, eta: string) => `Diantar sejak ${from} WIB · perkiraan tiba ${eta} WIB`,
    courierLate: 'Melewati batas waktu layanan',
    couriersOut: (n: string) => `${n} kurir sedang mengantar`,
    hubs: (n: string) => `${n} Hub`,
    couriersLate: (n: string) => `${n} melewati batas waktu layanan`,
    zoomForCouriers: 'Perbesar peta untuk melihat setiap kurir',
  },
  trip: {
    estimate: 'Perkiraan posisi',
    withdrawal: 'Penarikan',
    withdrawalNote: 'Surat Jalan penarikan: tabung dibawa ke Bengkel Uji untuk uji berkala atau karena rusak.',
    photoAlt: (plate: string) => `Foto kendaraan ${plate}`,
    estimateNote: 'Tanpa GPS: posisi diperkirakan dari waktu berangkat dan perkiraan lama perjalanan.',
    late: 'Melewati perkiraan tiba',
    departed: 'Berangkat',
    eta: 'Perkiraan tiba',
    progress: 'Perjalanan',
    progressAria: (pct: number) => `Perjalanan ${pct}%`,
    cylinders: 'Muatan',
    cylindersValue: (n: string) => `${n} tabung`,
    vehicle: 'Kendaraan',
    driver: 'Pengemudi',
    open: 'Lihat Surat Jalan',
    close: 'Tutup rincian kiriman',
    route: (from: string, to: string) => `${from} ke ${to}`,
    picked: (no: string) => `Kiriman ${no} dipilih`,
  },
} as const;
